import { useMemo } from "react";

const CANVAS_W = 1080;

const RTL_STRONG = /[\p{Script=Hebrew}\p{Script=Arabic}\p{Script=Syriac}\p{Script=Thaana}]/u;
const LTR_STRONG = /[\p{Script=Latin}\p{Script=Greek}\p{Script=Cyrillic}]/u;
function detectDir(text) {
  if (!text) return "ltr";
  for (const ch of text) {
    if (RTL_STRONG.test(ch)) return "rtl";
    if (LTR_STRONG.test(ch)) return "ltr";
  }
  return "ltr";
}

function hexToRgba(hex, alpha = 1) {
  const h = hex.replace("#", "");
  const len = h.length;
  if (len === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = len === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
  return `rgba(${r},${g},${b},${a * alpha})`;
}

function buildGradientCss(el) {
  // Template direction 0 = L→R; CSS 0deg = bottom→top. Convert: cssAngle = (dir + 90) % 360
  const cssAngle = (el.direction + 90) % 360;
  const sorted = [...el.stops].sort((a, b) => a.position - b.position);
  const stops = sorted
    .map((s) => `${hexToRgba(s.color, s.alpha)} ${Math.round(s.position * 100)}%`)
    .join(", ");
  return `linear-gradient(${cssAngle}deg, ${stops})`;
}

// Match reframer/overlay.py: 0.4s fade-in, 0.5s fade-out, capped at half the
// visible window so a very short overlay still gets a symmetric bell-curve.
const FADE_IN_DUR = 0.4;
const FADE_OUT_DUR = 0.5;
const GHOST_OPACITY = 0.15;

function timingOpacity(timing, t) {
  if (!timing || timing.mode === "full") return 1;
  let start, end, fadeInPref;
  if (timing.mode === "fromStart") {
    start = 0;
    end = timing.end;
    // Ships with the video — no entrance fade, only exit.
    fadeInPref = 0;
  } else if (timing.mode === "range") {
    start = timing.start;
    end = timing.end;
    fadeInPref = FADE_IN_DUR;
  } else {
    return 1;
  }
  if (start == null || end == null || !(end > start)) return 1;
  // Outside the timing window: ghost the layer so editors can still see it.
  if (t < start || t >= end) return GHOST_OPACITY;
  const half = (end - start) / 2;
  const fIn = Math.min(fadeInPref, half);
  const fOut = Math.min(FADE_OUT_DUR, half);
  if (fIn > 0 && t < start + fIn) return Math.max(GHOST_OPACITY, (t - start) / fIn);
  if (t > end - fOut) return Math.max(GHOST_OPACITY, (end - t) / fOut);
  return 1;
}

const FONT_FAMILY_MAP = {
  NotoSansHebrew: "Noto Sans Hebrew",
};
function cssFont(font) {
  return FONT_FAMILY_MAP[font] || font || "Heebo";
}

export default function OverlayLayer({
  template,
  textOverrides,
  timing,
  containerW,
  containerH,
  currentTime,
}) {
  const scale = useMemo(
    () => (containerW != null ? containerW / CANVAS_W : 1),
    [containerW]
  );

  const opacity = timingOpacity(timing, currentTime);

  if (!template) return null;

  const sorted = [...template.elements].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        opacity,
        transition: "opacity 0.25s ease",
      }}
    >
      {sorted.map((el) => {
        const base = {
          position: "absolute",
          left: el.x * scale,
          top: el.y * scale,
          width: el.w * scale,
          height: el.h * scale,
          opacity: el.opacity ?? 1,
          transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
          boxSizing: "border-box",
          overflow: "hidden",
        };

        if (el.type === "text") {
          const content = textOverrides?.[el.id] ?? el.defaultText ?? "";
          const dir = detectDir(content);
          const shadow = el.shadow
            ? `${el.shadow.offsetX * scale}px ${el.shadow.offsetY * scale}px ${el.shadow.blur * scale}px ${el.shadow.color}`
            : undefined;
          const outline = el.outline
            ? `${el.outline.width * scale}px ${el.outline.color}`
            : undefined;
          let textAlign;
          if (el.align === "center") textAlign = "center";
          else if (el.align === "end") textAlign = dir === "rtl" ? "left" : "right";
          else textAlign = dir === "rtl" ? "right" : "left";
          return (
            <div
              key={el.id}
              dir={dir}
              style={{
                ...base,
                fontSize: el.size * scale,
                fontFamily: cssFont(el.font),
                fontWeight: el.weight,
                color: el.color,
                lineHeight: el.lineHeight ?? 1.2,
                textShadow: shadow,
                WebkitTextStroke: outline,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
              }}
            >
              <div style={{ width: "100%", textAlign }}>{content}</div>
            </div>
          );
        }

        if (el.type === "logo") {
          if (!el.url) {
            return (
              <div
                key={el.id}
                style={{
                  ...base,
                  border: "1.5px dashed rgba(255,255,255,0.3)",
                  borderRadius: 4,
                }}
              />
            );
          }
          return (
            <img
              key={el.id}
              src={el.url}
              alt=""
              style={{
                ...base,
                objectFit: "contain",
              }}
            />
          );
        }

        if (el.type === "gradient") {
          return (
            <div
              key={el.id}
              style={{
                ...base,
                background: buildGradientCss(el),
              }}
            />
          );
        }

        if (el.type === "shape" && el.kind === "rect") {
          const combinedAlpha = (el.alpha ?? 1) * (el.opacity ?? 1);
          return (
            <div
              key={el.id}
              style={{
                ...base,
                opacity: combinedAlpha,
                backgroundColor: el.fill,
                borderRadius: el.radius * scale,
              }}
            />
          );
        }

        return null;
      })}
    </div>
  );
}
